import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Save, User as UserIcon, Loader, Shield, Search, Check, Database } from 'lucide-react';
import Select from 'react-select';
import { User, Industry, Company } from './../types/user';
import type { SavedQuery } from '../types/query';

export interface UserModalSaveData {
  name: string;
  email: string;
  industry: string;
  distributor: string;
  selectedServiceIds: string[];
}

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: UserModalSaveData) => void;
  user?: User;
  industries: Industry[];
  distributors: Company[];
  isSubmitting?: boolean;
  services: SavedQuery[];
  userPermissions: Set<string>;
  loadingPermissions?: boolean;
}

interface SelectOption {
  value: number;
  label: string;
}

export default function UserModal({
  isOpen,
  onClose,
  onSave,
  user,
  industries,
  distributors,
  isSubmitting = false,
  services,
  userPermissions,
  loadingPermissions = false
}: UserModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    industry: '',
    distributor: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedIndustries, setSelectedIndustries] = useState<SelectOption[]>([]);
  const [selectedDistributors, setSelectedDistributors] = useState<SelectOption[]>([]);
  const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);
  const [isDistributorDropdownOpen, setIsDistributorDropdownOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');

  const industryOptions = useMemo((): SelectOption[] =>
    industries.map((industry:any) => ({
      value: industry.value,
      label: industry.label
    })), [industries]
  );

  const distributorOptions = useMemo((): SelectOption[] =>
    distributors.map((distributor:any) => ({
      value: distributor.value,
      label: distributor.label
    })), [distributors]
  );

  const normalizeString = useCallback((str: string): string => {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/g, '')
      .trim();
  }, []);

  const processIndustryClipboardContent = useCallback((content: string): SelectOption[] => {
    const pastedIndustries: SelectOption[] = [];

    const industryNames = content.split('\n').map(name => name.trim()).filter(name => name.length > 0)
      .concat(content.split(' - ').map(name => name.trim()));

    industryNames.forEach(pastedName => {
      const normalizedPasted = normalizeString(pastedName);

      industryOptions.forEach(industry => {
        const normalizedIndustry = normalizeString(industry.label);

        if (normalizedPasted === normalizedIndustry) {
          pastedIndustries.push(industry);
          return;
        }

        if (normalizedIndustry.includes(normalizedPasted) || normalizedPasted.includes(normalizedIndustry)) {
          pastedIndustries.push(industry);
        }
      });
    });

    const uniqueIndustries = pastedIndustries.filter((industry, index, self) =>
      index === self.findIndex(i => i.value === industry.value)
    );

    return uniqueIndustries;
  }, [industryOptions, normalizeString]);

  const processDistributorClipboardContent = useCallback((content: string): SelectOption[] => {
    const pastedDistributors: SelectOption[] = [];

    const distributorNames = content.split('\n').map(name => name.trim()).filter(name => name.length > 0)
      .concat(content.split(' - ').map(name => name.trim()));

    distributorNames.forEach(pastedName => {
      const normalizedPasted = normalizeString(pastedName);

      distributorOptions.forEach(distributor => {
        const normalizedDistributor = normalizeString(distributor.label);

        if (normalizedPasted === normalizedDistributor) {
          pastedDistributors.push(distributor);
          return;
        }

        const pastedParts = normalizedPasted.split(/\s+/);
        const distributorParts = normalizedDistributor.split(/\s+/);

        const allPartsMatch = pastedParts.every(pastedPart =>
          distributorParts.some(distributorPart =>
            distributorPart === pastedPart ||
            (pastedPart.length > 2 && distributorPart.includes(pastedPart)) ||
            (distributorPart.length > 2 && pastedPart.includes(distributorPart))
          )
        );

        if (allPartsMatch && pastedParts.length >= 1) {
          pastedDistributors.push(distributor);
        }
      });
    });

    const uniqueDistributors = pastedDistributors.filter((distributor, index, self) =>
      index === self.findIndex(d => d.value === distributor.value)
    );

    return uniqueDistributors;
  }, [distributorOptions, normalizeString]);

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'v' && isIndustryDropdownOpen) {
        e.preventDefault();

        try {
          const text = await navigator.clipboard.readText();
          const matchedIndustries = processIndustryClipboardContent(text);

          if (matchedIndustries.length > 0) {
            const currentValues = selectedIndustries.map(i => i.value);
            const newIndustries = matchedIndustries.filter(i => !currentValues.includes(i.value));
            const updatedIndustries = [...selectedIndustries, ...newIndustries];
            setSelectedIndustries(updatedIndustries);

            const values = updatedIndustries.map(option => option.value).join(',');
            setFormData(prev => ({ ...prev, industry: values }));
          }
        } catch (err) {
          console.error('Erro ao acessar clipboard:', err);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isIndustryDropdownOpen, selectedIndustries, processIndustryClipboardContent]);

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'v' && isDistributorDropdownOpen) {
        e.preventDefault();

        try {
          const text = await navigator.clipboard.readText();
          const matchedDistributors = processDistributorClipboardContent(text);

          if (matchedDistributors.length > 0) {
            const currentValues = selectedDistributors.map(d => d.value);
            const newDistributors = matchedDistributors.filter(d => !currentValues.includes(d.value));
            const updatedDistributors = [...selectedDistributors, ...newDistributors];
            setSelectedDistributors(updatedDistributors);

            const values = updatedDistributors.map(option => option.value).join(',');
            setFormData(prev => ({ ...prev, distributor: values }));
          }
        } catch (err) {
          console.error('Erro ao acessar clipboard:', err);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDistributorDropdownOpen, selectedDistributors, processDistributorClipboardContent]);

  const customSelectStyles = useMemo(() => ({
    control: (provided: any, state: any) => ({
      ...provided,
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : 'none',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 9999
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
      color: state.isSelected ? 'white' : '#374151'
    })
  }), []);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        industry: user.industries,
        distributor: user.companies
      });

      const industryIds = user.industries.split(',').filter(id => id.trim()).map(id => parseInt(id.trim()));
      const distributorIds = user.companies.split(',').filter(id => id.trim()).map(id => parseInt(id.trim()));

      const selectedIndustriesOptions = industryOptions.filter(opt => industryIds.includes(opt.value));
      const selectedDistributorsOptions = distributorOptions.filter(opt => distributorIds.includes(opt.value));

      setSelectedIndustries(selectedIndustriesOptions);
      setSelectedDistributors(selectedDistributorsOptions);
    } else {
      setFormData({
        name: '',
        email: '',
        industry: '',
        distributor: ''
      });
      setSelectedIndustries([]);
      setSelectedDistributors([]);
    }
    setErrors({});
    setServiceSearchTerm('');
  }, [user, isOpen, industryOptions, distributorOptions]);

  useEffect(() => {
    setSelectedServices(new Set(userPermissions));
  }, [userPermissions]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const dataToSave: UserModalSaveData = {
        name: formData.name,
        email: formData.email,
        industry: formData.industry || '',
        distributor: formData.distributor || '',
        selectedServiceIds: Array.from(selectedServices)
      };
      onSave(dataToSave);
    }
  }, [formData, selectedServices, validateForm, onSave]);

  const handleChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const handleIndustryChange = useCallback((selectedOptions: SelectOption[] | null | any) => {
    const options = selectedOptions || [];
    setSelectedIndustries(options);
    const values = options.map((option:any) => option.value).join(',');
    handleChange('industry', values);
  }, [handleChange]);

  const handleDistributorChange = useCallback((selectedOptions: SelectOption[] | null | any) => {
    const options = selectedOptions || [];
    setSelectedDistributors(options);
    const values = options.map((option:any) => option.value).join(',');
    handleChange('distributor', values);
  }, [handleChange]);

  const handleToggleService = useCallback((serviceId: string) => {
    setSelectedServices(prev => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  }, []);

  const filteredServices = useMemo(() => {
    if (!serviceSearchTerm) return services;
    const term = serviceSearchTerm.toLowerCase();
    return services.filter(s =>
      s.name.toLowerCase().includes(term) ||
      s.endpoint_slug.toLowerCase().includes(term) ||
      s.description?.toLowerCase().includes(term)
    );
  }, [services, serviceSearchTerm]);

  const handleSelectAllServices = useCallback(() => {
    const allFilteredSelected = filteredServices.length > 0 &&
      filteredServices.every(s => selectedServices.has(s.id));

    setSelectedServices(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredServices.forEach(s => next.delete(s.id));
      } else {
        filteredServices.forEach(s => next.add(s.id));
      }
      return next;
    });
  }, [filteredServices, selectedServices]);

  if (!isOpen) return null;

  const allFilteredSelected = filteredServices.length > 0 &&
    filteredServices.every(s => selectedServices.has(s.id));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <UserIcon className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {user ? 'Editar Usuário' : 'Novo Usuário'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Digite o nome completo"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="usuario@empresa.com"
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Indústria
              </label>
              <Select
                value={selectedIndustries}
                isMulti={true}
                onMenuOpen={() => setIsIndustryDropdownOpen(true)}
                onMenuClose={() => setIsIndustryDropdownOpen(false)}
                onChange={handleIndustryChange}
                options={industryOptions}
                placeholder={isIndustryDropdownOpen ? "Selecione as industrias (Ctrl+V para colar)" : "Selecione as industrias"}
                styles={customSelectStyles}
                className="text-sm"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isSearchable={true}
              />
              {errors.industry && <p className="mt-1 text-sm text-red-600">{errors.industry}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Distribuidor
              </label>
              <Select
                value={selectedDistributors}
                isMulti={true}
                onMenuOpen={() => setIsDistributorDropdownOpen(true)}
                onMenuClose={() => setIsDistributorDropdownOpen(false)}
                onChange={handleDistributorChange}
                options={distributorOptions}
                placeholder={isDistributorDropdownOpen ? "Selecione os distribuidores (Ctrl+V para colar)" : "Selecione os distribuidores"}
                styles={customSelectStyles}
                className="text-sm"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isSearchable={true}
              />
              {errors.distributor && <p className="mt-1 text-sm text-red-600">{errors.distributor}</p>}
            </div>

            <div className="border-t border-gray-200 pt-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-semibold text-gray-900">Permissões de Serviços</span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {selectedServices.size} selecionado{selectedServices.size !== 1 ? 's' : ''}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleSelectAllServices}
                  disabled={loadingPermissions || filteredServices.length === 0}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {allFilteredSelected ? 'Desmarcar Todos' : 'Marcar Todos'}
                </button>
              </div>

              {services.length > 3 && (
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input
                    type="text"
                    placeholder="Buscar servicos..."
                    value={serviceSearchTerm}
                    onChange={(e) => setServiceSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {loadingPermissions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-5 h-5 animate-spin text-blue-600 mr-2" />
                    <span className="text-sm text-gray-500">Carregando serviços...</span>
                  </div>
                ) : filteredServices.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Database className="w-8 h-8 text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">
                      {serviceSearchTerm ? 'Nenhum serviço encontrado' : 'Nenhum serviço cadastrado'}
                    </p>
                  </div>
                ) : (
                  <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                    {filteredServices.map((service) => {
                      const isSelected = selectedServices.has(service.id);
                      return (
                        <button
                          key={service.id}
                          type="button"
                          onClick={() => handleToggleService(service.id)}
                          className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${
                            isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center mt-0.5 transition-colors ${
                            isSelected
                              ? 'border-blue-500 bg-blue-500'
                              : 'border-gray-300 bg-white'
                          }`}>
                            {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 truncate">{service.name}</span>
                              <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                service.is_active
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}>
                                {service.is_active ? 'Ativo' : 'Inativo'}
                              </span>
                            </div>
                            <code className="text-xs text-gray-500">{service.endpoint_slug}</code>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:bg-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{user ? 'Salvar' : 'Criar'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
